/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { Table } from 'antd/lib'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useInvestigationsServiceCreateInvestigationTestResultEndpoint,
  useInvestigationsServiceGetInvestigationTestEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { CustomItemRender, message, Text, Upload } from 'src/components'
import { useSessionContext } from 'src/contexts'
import { getFullName } from 'src/features/users/utils'
import { useFileHandler, useUser } from 'src/hooks'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useSuppliesColumns } from '../hooks'

type Props = {
  onCancel: () => void
  testId?: string
  typeId?: string
} & ModalProps

export const AddResultModal = ({
  onCancel,
  testId,
  typeId,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const { columns } = useSuppliesColumns()

  const { userData } = useUser()
  const { session } = useSessionContext()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const allValues = Form.useWatch([], form)

  const { onFileChange, isUploading, saveFiles } = useFileHandler({
    form,
    folder: `results`
  })

  const { data: investigation, isLoading } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(typeId) },
      [typeId],
      { enabled: !!typeId }
    )

  const { data: test } = useInvestigationsServiceGetInvestigationTestEndpoint(
    {
      id: String(testId)
    },
    [String(testId)],
    {
      enabled: !!testId
    }
  )

  const { mutate } =
    useInvestigationsServiceCreateInvestigationTestResultEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('investigations.successfully_added_result'))
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetInvestigationTestsEndpointKey]
        })
        onCancel()
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const updatedFiles = await saveFiles()

    const payloads = Object.entries(values.infection).map(
      ([key, value]: any) => ({
        ...value,
        testTypeId: key,
        resultDate: dayjs(values.resultDate).format(),
        testedBy: session?.userId,
        mediaItems: updatedFiles,
        docs: undefined
      })
    )

    for (const payload of payloads) {
      mutate({ id: String(testId), requestBody: payload })
    }
  }

  useEffect(() => {
    form.setFieldsValue({
      institution: test?.institution?.name,
      resultDate: dayjs()
    })
  }, [form, test])

  return (
    <Modal
      open
      width={1100}
      footer={null}
      maskClosable={false}
      title={t('analysis.add_one_result')}
      onCancel={onCancel}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={24}>
          <Card loading={isLoading}>
            <Text weight={400}>
              {t('investigations.laboratory_tehnic')}{' '}
              <Text weight={700}>
                {getFullName(userData?.firstName, userData?.lastName)}
              </Text>
            </Text>
          </Card>

          <Card>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  name="institution"
                  label={t('common.medical_institution')}
                  rules={requiredRules}
                >
                  <Input disabled />
                </Form.Item>
              </Col>

              {test?.supplies && (
                <Col span={24}>
                  <Table
                    rowKey={(record) => String(record.product?.id)}
                    bordered
                    scroll={{ x: 'max-content' }}
                    dataSource={test?.supplies}
                    columns={columns}
                    pagination={false}
                  />
                </Col>
              )}

              <Col span={12}>
                <Form.Item
                  name="resultDate"
                  label={t('tests.result_datetime')}
                  rules={requiredRules}
                >
                  <DatePicker
                    className="full-width"
                    showTime
                    showSecond={false}
                    disabledDate={disabledFutureDate}
                    format={dayMonthYearHourMinutesFormatWithDot}
                  />
                </Form.Item>
              </Col>

              {investigation?.testTypes?.map((item) => {
                const resultOptionId =
                  allValues?.infection?.[String(item.id)]?.resultOptionId

                return (
                  <React.Fragment key={item.id}>
                    <Col span={24}>
                      <Text size="xl">
                        {t('investigations.tested_at')}:{' '}
                        {item?.infection?.translation?.[currentLang?.value]}
                      </Text>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name={['infection', String(item.id), 'resultOptionId']}
                        label={t('tests_results.result')}
                        rules={requiredRules}
                      >
                        <Select
                          options={item?.resultOptions?.map((item) => ({
                            value: item.id,
                            label: item.translation?.[currentLang?.value]
                          }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name={[
                          'infection',
                          String(item.id),
                          'resultValueOptionId'
                        ]}
                        label={t('specimens.result_option')}
                      >
                        <Select
                          options={item?.resultOptions
                            ?.find((item) => item.id === resultOptionId)
                            ?.valueOptions?.map((item) => ({
                              value: item.id,
                              label: item.translation?.[currentLang?.value]
                            }))}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        name={['infection', String(item.id), 'resultValue']}
                        label={t('specimens.result_value')}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </React.Fragment>
                )
              })}

              <Col span={24}>
                <Form.Item name="docs" className="full-width">
                  <Upload
                    onChange={onFileChange}
                    customRequest={() => null}
                    itemRender={(_, file, __, { remove }) => (
                      <CustomItemRender file={file} onRemove={remove} />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <Button type="primary" loading={isUploading} onClick={onSubmit}>
              {t('roles.confirm')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
